import { ChangeEvent, useEffect, useState, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { Text, Flex, useToast, Skeleton, Switch, FormControl, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePutSocialMediaFeedMutation, useSocialMedia } from '@/services/queries/social-media';
import { socialMediaFormSchema, type SocialMediaFormData } from './schemas';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { breadcrumb } from './breadcrumb';

export const ApplicationSocialMediaFeed = () => {
  const [isAppAreaActive, setIsAppAreaActive] = useState(false);
  const { t } = useTranslation();

  const {
    data: appAreaData,
    isLoading: isLoadingAppArea,
    refetch: refetchAppArea,
  } = useAppApplicationComponentArea(ApplicationComponentId.SOCIAL_MEDIA_FEED);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_social_media.social_media_feed.toggled_enable')
      : t('pages.application_social_media.social_media_feed.toggled_disable');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_social_media.social_media_feed.failed_enable')
      : t('pages.application_social_media.social_media_feed.failed_disable');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.SOCIAL_MEDIA_FEED, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });

        refetchSocialMedia();
        refetchAppArea();
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const { data: socialMedia, isLoading: isLoadingSocialMedia, refetch: refetchSocialMedia } = useSocialMedia();
  const { mutate: updateSocialMedia, isLoading: isLoadingUpdateSocialMedia } = usePutSocialMediaFeedMutation();
  const navigate = useNavigate();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<SocialMediaFormData>({
    resolver: zodResolver(socialMediaFormSchema),
    defaultValues: {
      feed_twitter: false,
    },
  });

  const handleUpdateSocialMedia = (data: SocialMediaFormData) => {
    updateSocialMedia(
      {
        feed_twitter: data.feed_twitter,
      },
      {
        onSuccess: () => {
          toast({
            title: t('pages.application_social_media.social_media_changes_saved'),
            status: 'success',
          });

          refetchSocialMedia();
          refetchAppArea();
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_social_media.social_media_save_failed'),
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  useEffect(() => {
    if (!socialMedia) return;

    setValue('feed_twitter', socialMedia?.feed_twitter);
  }, [setValue, socialMedia]);

  return (
    <PageContent title={t('pages.application_social_media.social_media_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex alignItems='center' gap={4}>
          {isLoadingAppArea ? (
            <>
              <Skeleton w='130px' h='21px' />
              <Skeleton w='102px' h='21px' />
            </>
          ) : (
            <>
              <Text fontWeight='bold' color='brand.500'>
                {t('pages.application_social_media.social_media')}
              </Text>

              <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                {isAppAreaActive
                  ? t('pages.application_social_media.social_media_enabled')
                  : t('pages.application_social_media.social_media_disabled')}
              </Switch>
            </>
          )}
        </Flex>

        <Flex ref={formRef} as='form' onSubmit={handleSubmit(handleUpdateSocialMedia)} direction='column' gap={4} px={4}>
          {isLoadingSocialMedia ? (
            <Flex direction='column' gap={2}>
              <Skeleton w='120px' h='21px' />
              <Skeleton w='120px' h='21px' />
            </Flex>
          ) : (
            <Flex direction='column' gap={2}>
              <Controller
                name='feed_twitter'
                control={control}
                render={({ field }) => (
                  <FormControl alignSelf='center' display='flex' gap={2} alignItems='center' isInvalid={!!errors?.feed_twitter}>
                    <Switch
                      size='sm'
                      name={field.name}
                      ref={field.ref}
                      isChecked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                    <Text fontWeight='medium'>X (Twitter)</Text>
                  </FormControl>
                )}
              />
            </Flex>
          )}
        </Flex>

        <Flex justifyContent={{ base: 'flex-end', md: 'flex-start' }} gap={6}>
          <Button variant='link' color='brand.500' textDecoration='underline' onClick={() => navigate(-1)}>
            {t('pages.application_social_media.social_media_back')}
          </Button>
          <Button onClick={() => formRef?.current?.requestSubmit()}>
            {t('pages.application_social_media.social_media_save')}
          </Button>
        </Flex>
      </SectionBox>

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_social_media.social_media_title_label')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_social_media.social_media_description_enabled')
              : t('pages.application_social_media.social_media_description_disabled')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea || isLoadingUpdateSocialMedia} />
    </PageContent>
  );
};
